/* eslint-disable no-bitwise -- deterministic hash/xorshift for decorative peaks */
// Waveform peaks for audio attachments.
// - `decorativePeaks(seed, bars)`: deterministic bars shown immediately (no network, no decode),
//   so long conversations with many audios stay cheap to render.
// - `loadPeaks(url, bars)`: real peaks, computed lazily (first play) with fetch + decodeAudioData
//   and cached per URL. Falls back to null on any failure (CORS, codec, no AudioContext).

export const DEFAULT_BARS = 40;
const MAX_CACHE = 200;
const peaksCache = new Map();
const inflight = new Map();

const hashString = value => {
  let hash = 2166136261;
  const str = String(value || '');
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
};

export const decorativePeaks = (seed, bars = DEFAULT_BARS) => {
  let state = hashString(seed) || 1;
  const peaks = [];
  for (let i = 0; i < bars; i += 1) {
    // xorshift32 – cheap, deterministic per seed
    state ^= state << 13;
    state ^= state >>> 17;
    state ^= state << 5;
    const rand = (state >>> 0) / 4294967295;
    // bias towards a "speech" envelope: softer edges, livelier middle
    const envelope = 0.55 + 0.45 * Math.sin((Math.PI * (i + 0.5)) / bars);
    peaks.push(Math.max(0.12, Math.min(1, rand * envelope + 0.1)));
  }
  return peaks;
};

export const computePeaks = (audioBuffer, bars = DEFAULT_BARS) => {
  const channels = audioBuffer.numberOfChannels;
  const length = audioBuffer.length;
  const bucket = Math.max(1, Math.floor(length / bars));
  const peaks = new Array(bars).fill(0);
  for (let c = 0; c < channels; c += 1) {
    const data = audioBuffer.getChannelData(c);
    for (let b = 0; b < bars; b += 1) {
      const start = b * bucket;
      const end = b === bars - 1 ? length : Math.min(length, start + bucket);
      let sum = 0;
      for (let i = start; i < end; i += 1) sum += data[i] * data[i];
      const rms = Math.sqrt(sum / Math.max(1, end - start));
      if (rms > peaks[b]) peaks[b] = rms;
    }
  }
  const max = Math.max(...peaks, 0.0001);
  return peaks.map(p => Math.max(0.08, p / max));
};

const remember = (url, peaks) => {
  if (peaksCache.size >= MAX_CACHE) {
    peaksCache.delete(peaksCache.keys().next().value);
  }
  peaksCache.set(url, peaks);
  return peaks;
};

export const cachedPeaks = url => peaksCache.get(url) || null;

export const loadPeaks = async (url, bars = DEFAULT_BARS) => {
  if (!url) return null;
  if (peaksCache.has(url)) return peaksCache.get(url);
  if (inflight.has(url)) return inflight.get(url);

  const AudioContextClass = window.AudioContext || window.webkitAudioContext;
  if (!AudioContextClass || typeof window.fetch !== 'function') return null;

  const promise = (async () => {
    let context;
    try {
      const response = await fetch(url, { credentials: 'omit' });
      if (!response.ok) return null;
      const data = await response.arrayBuffer();
      context = new AudioContextClass();
      const buffer = await context.decodeAudioData(data);
      return remember(url, computePeaks(buffer, bars));
    } catch {
      return null;
    } finally {
      inflight.delete(url);
      if (context && typeof context.close === 'function') {
        context.close().catch(() => {});
      }
    }
  })();

  inflight.set(url, promise);
  return promise;
};

// test hook
export const resetPeaksCache = () => {
  peaksCache.clear();
  inflight.clear();
};
